/**
 * Native Event Hook
 *
 * 네이티브에서 웹으로 전달되는 이벤트를 구독하는 React Hook
 */

import { useEffect, useRef } from 'react'

import type { NativeEventAction, NativeEventGroup, NativeEventHandler } from '@/lib/bridge'
import { addNativeEventListener } from '@/lib/bridge'

/**
 * 네이티브 이벤트를 구독하는 React Hook
 *
 * @param group 이벤트 그룹
 * @param action 이벤트 액션
 * @param handler 이벤트 핸들러 함수
 *
 * @example
 * ```typescript
 * // 앱 상태 변경 감지
 * useNativeEvent('lifecycle', 'onAppStateChange', (data) => {
 *   console.log('App state:', data.state)
 *   if (data.state === 'background') {
 *     // 백그라운드 전환 시 처리
 *   }
 * })
 *
 * // 네이티브 백 버튼 처리
 * useNativeEvent('navigation', 'onNativeBack', () => {
 *   // 모달이나 팝업이 있으면 닫기
 *   // 없으면 네이티브에 뷰 종료 요청
 * })
 * ```
 */
export function useNativeEvent<TGroup extends NativeEventGroup, TAction extends NativeEventAction<TGroup>>(
  group: TGroup,
  action: TAction,
  handler: NativeEventHandler<TGroup, TAction>
): void {
  // 최신 핸들러를 참조하기 위한 ref
  const handlerRef = useRef(handler)

  // 핸들러가 변경될 때마다 ref 업데이트
  useEffect(() => {
    handlerRef.current = handler
  }, [handler])

  // 이벤트 리스너 등록 (handler는 의존성에서 제외하여 불필요한 재등록 방지)
  useEffect(() => {
    const unsubscribe = addNativeEventListener(group, action, data => {
      // 최신 핸들러 호출
      handlerRef.current(data)
    })

    return () => {
      unsubscribe()
    }
  }, [group, action])
}
